/**
 * Product Service
 * Handles product data management and operations
 */

import { 
  DIRECT_PRODUCTS, 
  getProductById as getDirectProductById,
  getProductsByCategory as getDirectProductsByCategory,
  getFeaturedProducts as getDirectFeaturedProducts,
  getNewArrivals as getDirectNewArrivals,
  getSpecialOffers as getDirectSpecialOffers,
  searchProducts as searchDirectProducts
} from '../data/ProductData';

// Replace with your actual domain
const API_URL = 'https://nmdiamond.in/api/products/manage.php';

// Use direct products as fallback
let cachedProducts = [...DIRECT_PRODUCTS];
let lastFetchTime = Date.now();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all products from API with fallback to direct products
 * @returns {Promise<Array>} Array of products
 */
const fetchProducts = async () => {
  try {
    console.log('Fetching products from API:', API_URL);
    const response = await fetch(API_URL);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error (${response.status}):`, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      console.error('API returned error:', data.error);
      throw new Error(data.error);
    }
    
    // Process the products to ensure consistent format
    const products = (data.products || []).map(product => {
      // Ensure boolean values
      const processedProduct = {
        ...product,
        inStock: Boolean(product.inStock),
        featured: Boolean(product.featured),
        discount: parseFloat(product.discount || 0),
        price: parseFloat(product.price || 0),
        // Ensure category is never null
        category: product.category || 'uncategorized',
        // Ensure pageLocation is an array
        pageLocation: Array.isArray(product.pageLocation) ? product.pageLocation : []
      };
      
      // Fix image paths
      if (processedProduct.image) {
        if (!processedProduct.image.startsWith('http') && !processedProduct.image.startsWith('/')) {
          processedProduct.image = '/' + processedProduct.image;
        }
      } else {
        processedProduct.image = '/media/placeholder.jpeg';
      }
      
      return processedProduct;
    });
    
    console.log(`Successfully fetched ${products.length} products from API`);
    return products;
  } catch (error) {
    console.error(`Error fetching products: ${error.message}`);
    console.warn('Using direct products as fallback');
    return [...DIRECT_PRODUCTS];
  }
};

/**
 * Initialize products
 */
export const initializeProducts = async () => {
  // Check if cache is still valid
  const now = Date.now();
  const cacheExpired = now - lastFetchTime > CACHE_DURATION;
  
  if (cacheExpired || cachedProducts.length === 0) {
    console.log('Cache expired or empty, fetching fresh products');
    try {
      const products = await fetchProducts();
      cachedProducts = products;
      lastFetchTime = now;
      return products;
    } catch (error) {
      console.error('Error initializing products:', error);
      return cachedProducts;
    }
  } else {
    console.log('Using cached products');
    return cachedProducts;
  }
};

/**
 * Get all products
 * @returns {Promise<Array>} Array of all products
 */
export const getAllProducts = async () => {
  try {
    const products = await fetchProducts();
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    // Fallback to direct products if API fails
    return DIRECT_PRODUCTS;
  }
};

/**
 * Get all in-stock products
 * @returns {Promise<Array>} Array of in-stock products
 */
export const getAllInStockProducts = async () => {
  try {
    const products = await fetchProducts();
    return products.filter(product => product.inStock);
  } catch (error) {
    console.error('Error getting in-stock products:', error);
    return cachedProducts.filter(product => product.inStock);
  }
};

/**
 * Get products by category
 * @param {string} category - Category to filter by
 * @returns {Promise<Array>} Array of products in the specified category
 */
export const getProductsByCategory = async (category) => {
  try {
    const products = await fetchProducts();
    return products.filter(product => 
      product.category === category && product.inStock
    );
  } catch (error) {
    console.error(`Error getting products by category ${category}:`, error);
    return getDirectProductsByCategory(category);
  }
};

/**
 * Get featured products
 * @param {number} limit - Maximum number of products to return
 * @returns {Promise<Array>} Array of featured products
 */
export const getFeaturedProducts = async (limit = 5) => {
  try {
    const products = await fetchProducts();
    return products
      .filter(product => product.featured && product.inStock)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting featured products:', error);
    return getDirectFeaturedProducts(limit);
  }
};

/**
 * Get new arrivals
 * @param {number} limit - Maximum number of products to return
 * @returns {Promise<Array>} Array of new products
 */
export const getNewArrivals = async (limit = 5) => {
  try {
    const products = await fetchProducts();
    return [...products]
      .filter(product => product.inStock)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting new arrivals:', error);
    return getDirectNewArrivals(limit);
  }
};

/**
 * Get special offers
 * @param {number} limit - Maximum number of products to return
 * @returns {Promise<Array>} Array of products with discounts
 */
export const getSpecialOffers = async (limit = 5) => {
  try {
    const products = await fetchProducts();
    return products
      .filter(product => product.inStock && product.discount > 0)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting special offers:', error);
    return getDirectSpecialOffers(limit);
  }
};

/**
 * Get product by ID
 * @param {string} id - Product ID
 * @param {boolean} includeOutOfStock - Whether to include out-of-stock products
 * @returns {Promise<Object|null>} Product object or null if not found
 */
export const getProductById = async (id, includeOutOfStock = false) => {
  try {
    const products = await fetchProducts();
    const product = products.find(p => p.id === id);
    
    if (!product) {
      return null;
    }
    
    if (!includeOutOfStock && !product.inStock) {
      return null;
    }
    
    return product;
  } catch (error) {
    console.error(`Error getting product by ID ${id}:`, error);
    return getDirectProductById(id);
  }
};

/**
 * Add product
 * @param {Object} productData - Product to add
 * @returns {Promise<Object>} Added product
 */
export const addProduct = async (productData) => {
  try {
    console.log('Adding product to API:', productData);
    
    // Generate a consistent ID if not provided
    if (!productData.id) {
      productData.id = `product_${Date.now()}`;
    }
    
    // Ensure boolean values are converted to integers for the database
    const preparedData = {
      ...productData,
      featured: productData.featured ? 1 : 0,
      inStock: productData.inStock ? 1 : 0,
      // Ensure price is in the correct format
      price: productData.price || 'Request for price',
      // Ensure category is never null
      category: productData.category || 'uncategorized',
      // Ensure description is never null
      description: productData.description || '',
      // Ensure image path is correct
      image: productData.image || '/media/placeholder.jpeg'
    };
    
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preparedData)
    });
    
    // Try to get response text for better error reporting
    let responseText;
    try {
      responseText = await response.text();
    } catch (e) {
      responseText = 'Could not read response text';
    }
    
    if (!response.ok) {
      console.error(`API error (${response.status}):`, responseText);
      throw new Error(`API error: ${response.status} - ${responseText}`);
    }
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response as JSON:', responseText);
      throw new Error('Invalid JSON response from server');
    }
    
    if (data.error) {
      console.error('API returned error:', data.error);
      throw new Error(data.error);
    }
    
    console.log('Product added successfully:', data);
    
    // Refresh cache
    cachedProducts = await fetchProducts();
    lastFetchTime = Date.now();
    
    return { ...productData, id: data.id };
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
};

/**
 * Update product
 * @param {string} id - Product ID
 * @param {Object} productData - Updated product data
 * @returns {Promise<Object|null>} Updated product or null if not found
 */
export const updateProduct = async (id, productData) => {
  try {
    const response = await fetch(API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...productData, id })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Refresh cache
    cachedProducts = await fetchProducts();
    lastFetchTime = Date.now();
    
    return productData;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Delete product
 * @param {string} id - Product ID
 * @returns {Promise<boolean>} True if deleted, false if not found
 */
export const deleteProduct = async (id) => {
  try {
    const response = await fetch(API_URL, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Refresh cache
    cachedProducts = await fetchProducts();
    lastFetchTime = Date.now();
    
    return true;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

/**
 * Search products
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Array of matching products
 */
export const searchProducts = async (searchTerm) => {
  try {
    const products = await fetchProducts();
    
    if (!searchTerm) return products.filter(p => p.inStock);
    
    const term = searchTerm.toLowerCase();
    return products.filter(product => 
      product.inStock && (
        product.name.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term) ||
        product.category.toLowerCase().includes(term)
      )
    );
  } catch (error) {
    console.error(`Error searching products with term "${searchTerm}":`, error);
    return searchDirectProducts(searchTerm);
  }
};

/**
 * Get sample products for initializing the store
 * @returns {Array} Array of sample product objects
 */
export const getSampleProducts = () => {
  return DIRECT_PRODUCTS;
};

/**
 * Manually add products directly to localStorage and memory cache
 * This can be used when the backend is down or for initializing products
 * @param {Array} products - Array of product objects to add
 * @returns {Array} The added products
 */
export const manuallyAddProducts = (products) => {
  return products;
};

/**
 * Sync products from frontend to database
 * This ensures all products in the frontend are also in the database
 * @returns {Promise<Object>} Result of the sync operation
 */
export const syncProductsToDatabase = async () => {
  try {
    console.log('Syncing products to database...');
    
    // Get all direct products
    const products = DIRECT_PRODUCTS.map(product => ({
      ...product,
      // Ensure all required fields are present
      id: product.id || `direct${Date.now()}`,
      name: product.name || 'Product Name',
      category: product.category || 'uncategorized',
      price: product.price || 'Request for price',
      description: product.description || '',
      image: product.image || '/media/placeholder.jpeg'
    }));
    
    const response = await fetch('https://nmdiamond.in/api/products/sync.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ products })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    console.log('Products synced successfully:', data);
    
    // Refresh cache
    cachedProducts = await fetchProducts();
    lastFetchTime = Date.now();
    
    return data;
  } catch (error) {
    console.error('Error syncing products:', error);
    throw error;
  }
};

/**
 * Sync all products to database using the dedicated sync_all endpoint
 * This ensures all 21 products are properly inserted into the database
 * @returns {Promise<Object>} Result of the sync operation
 */
export const syncAllProductsToDatabase = async () => {
  try {
    console.log('Syncing all products to database using dedicated endpoint...');
    
    const response = await fetch('https://nmdiamond.in/api/products/sync_all.php');
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    console.log('All products synced successfully:', data);
    
    // Refresh cache
    cachedProducts = await fetchProducts();
    lastFetchTime = Date.now();
    
    return data;
  } catch (error) {
    console.error('Error syncing all products:', error);
    throw error;
  }
};

export default {
  getAllProducts,
  getAllInStockProducts,
  getProductsByCategory,
  getFeaturedProducts,
  getNewArrivals,
  getSpecialOffers,
  getProductById,
  addProduct,
  updateProduct,
  deleteProduct,
  searchProducts,
  manuallyAddProducts,
  getSampleProducts,
  syncProductsToDatabase,
  syncAllProductsToDatabase,
  DIRECT_PRODUCTS
}; 